import { useEffect, useState } from "react";
import Editor from "@/components/editor/tiptap";
import ChatInterface from "@/components/chat-interface";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InkPen from "@/assets/icons/ink_pen.svg";
import BookIcon from "@/assets/icons/book.png";
import OpenBook from "@/assets/icons/open-book.png";

import StarIcon from "@/assets/icons/stars.svg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import useChatStore from "@/store/useChatStore";
import { useCurrentProject } from "@/store/useEditorStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiService } from "@/services/api-service";
import { toast } from "react-toastify";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useDraftActions } from "@/store/useDraftStore";
import { AttachedContextMaterials } from "@/components/attached-context-materials";

export const Route = createFileRoute("/app/_layout/editor/$projectId")({
  component: EditorWrapper,
  validateSearch(search) {
    return {
      type: (search?.type as string) || undefined,
    };
  },
});

function EditorWrapper() {
  const [isEditing, setIsEditing] = useState(false);
  const project = useCurrentProject();
  const { projectId: id } = Route.useParams();

  const queryClient = useQueryClient();

  const [docName, setDocName] = useState("Untitled");

  const renameProjectMutation = useMutation({
    mutationFn: ({ id, docName }: { id: string; docName: string }) =>
      ApiService.renameProject(id, docName),

    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [`project-${id}`] });
    },

    onError: (error: any) => {
      toast.error(`Failed to rename document: ${error.message}`);
    },
  });

  useEffect(() => {
    setDocName(project?.name as string);
  }, [project?.name]);

  const { type } = Route.useSearch();

  const { isOpen, openPanel } = useChatStore();
  const handleTitleEdit = () => {
    setIsEditing(true);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocName(e.target.value);
  };

  const handleTitleSave = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      renameProjectMutation.mutate({ docName, id });
      setIsEditing(false);
    }
  };

  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  // clear audios when path changes
  const { pathname } = useLocation();
  const { clearRecordings } = useDraftActions();
  useEffect(() => {
    clearRecordings();
  }, [pathname, clearRecordings]);

  const handleGetSuggestedName = () => {
    if (
      docName === "Untitled" &&
      project &&
      project.current_content?.length > 100
    ) {
      ApiService.getSuggestedProjectName(id).then((res) => {
        setDocName(res.name);
        renameProjectMutation.mutate({ docName: res.name, id });
      });
    }
  };

  return (
    <main className="">
      <div className="mt-5 flex flex-wrap gap-x-8">
        <Tabs
          defaultValue="editor"
          className="w-[400px] flex-1 relative sm:-top-10"
        >
          {isOffline && (
            <AnimatePresence>
              <motion.div
                key="offline-banner"
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -100, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="absolute top-0 left-1/2 transform -translate-x-1/2 z-50"
              >
                <div className="bg-red-500 text-neutral-100 px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 text-xs font-medium whitespace-nowrap">
                  <AlertCircle className="w-4 h-4" />
                  <span>No internet connection. You're offline.</span>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
          {!type && (
            <TabsList className="grid sm:max-w-md mx-auto grid-cols-2 h-full max-h-10 mb-5">
              <TabsTrigger value="editor">
                <img src={BookIcon} alt="book" className="w-4 h-4" />

                {isEditing ? (
                  <input
                    type="text"
                    value={docName}
                    onChange={handleTitleChange}
                    onKeyDown={handleTitleSave}
                    onBlur={() => setIsEditing(false)}
                    autoFocus
                    className="text-xs px-1 py-0.5 border rounded-sm"
                    disabled={renameProjectMutation.isPending}
                  />
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span
                          className="text-xs"
                          onDoubleClick={handleTitleEdit}
                          onMouseEnter={handleGetSuggestedName}
                        >
                          <span className="truncate hidden sm:block">
                            {docName?.length > 15
                              ? `${docName?.substring(0, 20)}...`
                              : docName}
                          </span>
                          <span className="truncate sm:hidden">
                            {docName?.length > 10
                              ? `${docName?.substring(0, 15)}...`
                              : docName}
                          </span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Double click to edit document name</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {!isEditing && (
                  <img
                    src={InkPen}
                    alt="ink pen"
                    className="w-4 h-4 cursor-pointer"
                    onClick={handleTitleEdit}
                  />
                )}
              </TabsTrigger>

              <TabsTrigger value="context">
                <span className="text-xs">Context</span>
                <img src={OpenBook} alt="open book" className="w-4 h-4" />
              </TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="editor">
            <div className="relative w-full">
              <div
                className={` ${
                  isOpen ? " w-full" : " max-w-4xl 2xl:max-w-5xl w-full mx-auto"
                } relative`}
              >
                <Editor />
              </div>

              {!isOpen && !type && (
                <button
                  className={`fixed bottom-10 right-0 sm:right-6 xl:right-16 2xl:right-60 cursor-pointer `}
                  onClick={openPanel}
                >
                  <img src={StarIcon} alt="brain" className="w-8 h-8" />
                </button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="context">
            <AttachedContextMaterials />
          </TabsContent>
        </Tabs>

        <div
          className={`hidden md:block relative top-6 max-w-sm md:max-w-full `}
        >
          <ChatInterface />
        </div>
      </div>
    </main>
  );
}
